<template>
  <v-container fluid class="invoice-list-page no-print pa-0">
    
    <ReceivePayment
      :CustomerId="selectedInvoice ? selectedInvoice.CustomerId : null"
      :createDialog="receivePaymentDialog"
      @close="receivePaymentDialog = false"
      @submit="getList()"
      :invoice="selectedInvoice"
      :invoiceReceivePayment="true"
    />

    <v-card :loading="loading" outlined class="invoice-list-card">
      <div class="invoice-list-header">
        <div class="invoice-title-wrap">
          <v-btn v-if="!CustomerId" icon small class="mr-2" @click="$router.go(-1)">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>

          <div>
            <div class="page-title">Invoice List</div>
            <div class="page-subtitle">
              {{ totalItems }} invoice{{ totalItems === 1 ? "" : "s" }} found
            </div>
          </div>
        </div>

        <div class="invoice-actions">
          <v-btn
            v-if="checkRightStatus(6)"
            small
            rounded
            color="primary"
            dark
            class="new-list-button"
            @click="add"
          >
            <v-icon left small>mdi-plus</v-icon>
            <span class="new-button-label">New</span>
          </v-btn>

          <v-btn
            v-if="InvoiceDetails.length !== 0 && checkRightStatus(6)"
            small
            rounded
            outlined
            @click="openRunning"
          >
            <v-icon left small>mdi-history</v-icon>
            Running Invoice
          </v-btn>
        </div>
      </div>

      <div class="invoice-filter-bar">
        <v-text-field
          v-model="search"
          outlined
          prepend-inner-icon="search"
          autofocus
          dense
          label="Search invoice, customer"
          single-line
          hide-details
          clearable
          class="invoice-search"
        />

        <template v-if="$vuetify.breakpoint.mdAndUp">
          <v-btn-toggle
            v-model="invoiceStatus"
            @change="onInvoiceStatusChange"
            dense
            mandatory
            class="status-toggle"
          >
            <v-btn value="all" small>All</v-btn>
            <v-btn value="open" small>Open</v-btn>
            <v-btn value="paid" small>Paid</v-btn>
          </v-btn-toggle>

          <v-checkbox
            v-if="invoiceStatus === 'open'"
            v-model="openAllDates"
            label="Include all dates"
            hide-details
            dense
            class="ma-0"
          />

          <v-spacer />

          <DateTimePicker
            v-if="invoiceStatus !== 'open' || !openAllDates"
            :defaultRange="defaultRange"
            :range="true"
            :hideTime="true"
            v-model="dateTime"
            @submit="getList()"
          />
        </template>

        <v-btn
          v-else
          icon
          @click="mobileFilterSheet = true"
        >
          <v-icon>mdi-tune-variant</v-icon>
        </v-btn>
      </div>

      <v-bottom-sheet
        v-model="mobileFilterSheet"
        inset
        max-width="520"
      >
        <v-card class="mobile-filter-card">
          <div class="mobile-filter-header">
            <v-btn icon @click="mobileFilterSheet = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>

            <div class="mobile-filter-title">Filters</div>
          </div>

          <v-card-text>
            <v-btn-toggle
              v-model="invoiceStatus"
              @change="onInvoiceStatusChange"
              mandatory
              dense
              class="mobile-status-toggle"
            >
              <v-btn value="all">All</v-btn>
              <v-btn value="open">Open</v-btn>
              <v-btn value="paid">Paid</v-btn>
            </v-btn-toggle>

            <v-divider class="my-4" />

            <DateTimePicker
              v-if="invoiceStatus !== 'open' || !openAllDates"
              :defaultRange="defaultRange"
              :range="true"
              :hideTime="true"
              v-model="dateTime"
              @submit="getList()"
            />

            <v-select
              v-model="itemsPerPage"
              :items="[10, 25, 50, 100]"
              label="Rows per page"
              outlined
              dense
              hide-details
              class="mt-4"
            />

            <v-checkbox
              v-if="invoiceStatus === 'open'"
              v-model="openAllDates"
              label="Include all dates"
              hide-details
              class="mt-4"
            />

            <v-btn
              block
              color="primary"
              rounded
              depressed
              class="mt-4"
              @click="mobileFilterSheet = false"
            >
              Apply
            </v-btn>
          </v-card-text>
        </v-card>
      </v-bottom-sheet>

      <v-card-text class="pa-4">
        <div class="summary-row mb-4">
          <div class="summary-box">
            <div class="summary-label">List Total</div>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div class="summary-value" v-bind="attrs" v-on="on">
                  {{ compactCurrency(listTotal) }}
                </div>
              </template>
              <span>{{ fullCurrency(listTotal) }}</span>
            </v-tooltip>
          </div>

          <div class="summary-box">
            <div class="summary-label">List Paid</div>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div class="summary-value" v-bind="attrs" v-on="on">
                  {{ compactCurrency(listPayment) }}
                </div>
              </template>
              <span>{{ fullCurrency(listPayment) }}</span>
            </v-tooltip>
          </div>

          <div class="summary-box">
            <div class="summary-label">List Due</div>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div class="summary-value" v-bind="attrs" v-on="on">
                  {{ compactCurrency(listBalance) }}
                </div>
              </template>
              <span>{{ fullCurrency(listBalance) }}</span>
            </v-tooltip>
          </div>

          <div class="summary-box">
            <div class="summary-label">Result Due</div>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div class="summary-value" v-bind="attrs" v-on="on">
                  {{ compactCurrency(fullBalance) }}
                </div>
              </template>
              <span>{{ fullCurrency(fullBalance) }}</span>
            </v-tooltip>
          </div>
        </div>

        <v-data-table
          :mobile-breakpoint="0"
          :headers="headers"
          :items="Invoices"
          :loading="loading"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :page.sync="page"
          :items-per-page.sync="itemsPerPage"
          :server-items-length="totalItems"
          :hide-default-footer="$vuetify.breakpoint.xsOnly"
          @current-items="updateTotal"
          :footer-props="{ 'items-per-page-options': [10, 15, 25, 50, 100] }"
          class="invoice-table"
        >
          <template v-slot:item="{ item }">
            <tr class="pointer invoice-row">
              <td class="text-left" @click="viewInvoice(item)">
                {{ item.date | moment("L LT") }}
              </td>

              <td class="text-left" @click="viewInvoice(item)">
                <span
                  v-if="item.dueDate"
                  :class="dueDateColor(item.dueDate)"
                >
                  {{ item.dueDate | moment("L") }}
                </span>
                <span v-else class="red--text font-weight-bold">Due now</span>
              </td>

              <td class="text-left" @click="viewInvoice(item)">
                <span class="font-weight-bold pr-1">{{ item.localId }}</span>

                <v-chip
                  v-if="Number(item.due || 0) === 0"
                  color="green lighten-5"
                  text-color="green darken-3"
                  small
                  class="font-weight-bold"
                >
                  Paid
                </v-chip>

                <v-chip
                  v-else
                  color="orange lighten-5"
                  text-color="orange darken-3"
                  small
                  class="font-weight-bold"
                >
                  Open
                </v-chip>

                <div
                  v-if="
                    currentBusiness &&
                    currentBusiness.metadata &&
                    !currentBusiness.metadata.isSingleCustomerPricing &&
                    item.PriceTier
                  "
                  class="mt-1"
                >
                  <v-chip
                    small
                    class="font-weight-bold"
                    color="red"
                    text-color="white"
                  >
                    {{ item.PriceTier.name }}
                  </v-chip>
                </div>

                <div v-if="item.EmailEvents && item.EmailEvents.length" class="mt-1">
                  <v-chip
                    small
                    :color="item.EmailEvents[0].openCount ? 'green lighten-5' : 'blue lighten-5'"
                    :text-color="item.EmailEvents[0].openCount ? 'green darken-3' : 'blue darken-3'"
                  >
                    {{ item.EmailEvents[0].openCount ? `Opened ${item.EmailEvents[0].openCount}x` : "Email sent" }}
                  </v-chip>
                </div>
              </td>

              <td
                v-if="!CustomerId"
                class="text-left"
                @click="viewInvoice(item)"
              >
                <div>{{ item.Customer ? item.Customer.name : "Unknown Customer" }}</div>
              </td>

              <td class="text-right font-weight-bold" @click="viewInvoice(item)">
                {{ item.total | currency }}
              </td>

              <td class="text-right" @click="viewInvoice(item)">
                {{ item.payment | currency }}
              </td>

              <td class="text-right font-weight-bold" @click="viewInvoice(item)">
                {{ item.due | currency }}
              </td>

              <td class="justify-end align-center text-right">
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon small>
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list dense class="menu-button">
                    <v-list-item @click="viewInvoice(item)">
                      <v-list-item-title>View Invoice</v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="viewPackingSlip(item)">
                      <v-list-item-title>View Packing Slip</v-list-item-title>
                    </v-list-item>

                    <Email
                      :list-item="true"
                      :id="item.id"
                      :displayId="item.localId"
                      type="invoice"
                      :postfix="`view?key=${currentBusiness.code}`"
                      :name="currentBusiness.name"
                      :email="item.Customer && item.Customer.email"
                      :obj="item.Customer"
                      :balance="item.due"
                      :dueDate="item.dueDate"
                      @sent="getList"
                    />

                    <v-list-item
                      v-if="Number(item.due || 0) !== 0"
                      @click="receivePayment(item)"
                    >
                      <v-list-item-title>Receive Payment</v-list-item-title>
                    </v-list-item>

                    <v-divider v-if="!CustomerId" />

                    <v-list-item v-if="!CustomerId && item.CustomerId" @click="viewProfile(item)">
                      <v-list-item-title>Profile</v-list-item-title>
                    </v-list-item>

                    <v-divider />

                    <v-list-item @click="copyInvoice(item)">
                      <v-list-item-title>Copy Invoice</v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="editInvoice(item)">
                      <v-list-item-title>Edit</v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="deleteInvoice(item)">
                      <v-list-item-title class="red--text">
                        Delete
                      </v-list-item-title>
                    </v-list-item>

                    <ShareLink
                      :id="item.id"
                      :postfix="`view?key=${currentBusiness.code}`"
                      type="invoice"
                      :text="`Invoice from ${currentBusiness.name} click the link to view`"
                    />

                    <v-divider />

                    <v-list-item @click="openSignature(item)">
                      <v-list-item-title>Receive Signature</v-list-item-title>
                    </v-list-item>

                    <v-divider />

                    <v-list-item @click="openHistory(item)">
                      <v-list-item-title>View History</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>

          <v-alert slot="no-results" :value="true" color="error" icon="warning">
            Your search for "{{ search }}" found no results.
          </v-alert>

        </v-data-table>

        <v-pagination
          v-if="$vuetify.breakpoint.xsOnly && mobilePageCount > 1"
          v-model="page"
          :length="mobilePageCount"
          :total-visible="5"
          circle
          class="mobile-pagination mt-3"
        />
      </v-card-text>
    </v-card>

    <InvoiceHistoryDialog
      v-show="historyDialog"
      v-model="historyDialog"
      :invoice="historyInvoice"
    />

    <SignaturePadDialog
      v-model="signatureDialog"
      :invoice="selectedInvoice"
      @save-signature="saveSignature"
    />

    <v-dialog v-model="loadingDialog" hide-overlay persistent width="300">
      <v-card color="primary" dark>
        <v-container>
          <div class="mb-3">Please stand by deleting invoice</div>
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          />
        </v-container>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import invoiceService from "../service";
import ShareLink from "@/components/ShareLink";
import Email from "@/components/EmailOrders";
import InvoiceHistoryDialog from "@/modules/Invoice/components/InvoiceHistoryDialog.vue";
import SignaturePadDialog from "@/components/SignaturePad.vue";
import Hashids from "hashids";
import _ from "lodash";

const DateTimePicker = () => import("@/components/DateTimePicker");
const ReceivePayment = () =>
  import("@/modules/Customer/Payment/components/Create.vue");

const hashids = new Hashids();

export default {
  name: "invoice-list",

  props: {
    CustomerId: {
      default: null,
    },
    showAll: {
      default: false,
    },
    due: {
      default: false,
    },
    initialStatus: {
      type: String,
      default: "all",
      validator: value => ["all", "open", "paid"].includes(value),
    },
  },

  components: {
    DateTimePicker,
    ShareLink,
    Email,
    ReceivePayment,
    InvoiceHistoryDialog,
    SignaturePadDialog,
  },

  data() {
    return {
      historyDialog: false,
      historyInvoice: null,
      mobileFilterSheet: false,
      totalItems: 0,
      invoiceStatus: this.initialStatus,
      loadingDialog: false,
      openAllDates: true,
      fullTotal: 0,
      fullPayment: 0,
      fullBalance: 0,
      listTotal: 0,
      listPayment: 0,
      listBalance: 0,
      dateTime: {
        start: this.$moment()
          .startOf("day")
          .subtract(60, "days")
          .toISOString(),
        end: this.$moment().endOf("day").toISOString(),
      },
      headers: [],
      Invoices: [],
      search: null,
      loading: true,
      page: 1,
      sortBy: "localId",
      pageCount: 0,
      sortDesc: true,
      itemsPerPage: 10,
      receivePaymentDialog: false,
      selectedInvoice: null,
      signatureDialog: false,
    };
  },

  computed: {
    ...mapGetters("global", [
      "currentUser",
      "checkRightStatus",
      "currentBusiness",
    ]),
    ...mapGetters("invoice", ["InvoiceDetails", "getEditInvoiceId"]),

    hasInvoiceViewLimit() {
      return !!(
        this.currentBusiness &&
        this.currentBusiness.metadata &&
        this.currentBusiness.metadata.invoiceViewLimit
      );
    },

    defaultRange() {
      if (this.hasInvoiceViewLimit) {
        return this.currentBusiness.metadata.invoiceViewLimit.range;
      }

      return "since60DaysAgo";
    },
    mobilePageCount() {
      return Math.max(1, Math.ceil(this.totalItems / this.itemsPerPage));
    },
  },

  watch: {
    search: _.debounce(function () {
      this.page = 1;
      this.getList();
    }, 300),

    page() {
      this.getList();
    },

    itemsPerPage() {
      this.page = 1;
      this.getList();
    },

    sortBy() {
      this.page = 1;
      this.getList();
    },

    sortDesc() {
      this.page = 1;
      this.getList();
    },

    CustomerId: {
      immediate: true,
      handler() {
        // Embedded customer profiles should return to their requested default
        // whenever the user selects a different customer.
        if (this.CustomerId) {
          this.invoiceStatus = this.initialStatus;
          if (this.initialStatus === "open") this.openAllDates = true;
          this.page = 1;
        }
        this.getList();
      },
    },

    openAllDates() {
      this.page = 1;
      this.getList();
    },
  },

  created() {
    this.loadHeader();
    this.$events.listen("invoiceListEvent", () => this.getList());
    document.title = "smplsale - Invoice List";
  },

  beforeDestroy() {
    this.$events.remove("invoiceListEvent");
  },

  methods: {
    onInvoiceStatusChange(status) {
      this.page = 1;
      if (status === "open") {
        const wasAlreadyAllDates = this.openAllDates;
        this.openAllDates = true;
        if (wasAlreadyAllDates) this.getList();
        return;
      }
      this.getList();
    },
    compactCurrency(value) {
      const amount = Number(value || 0);
      const absolute = Math.abs(amount);
      let divisor = 1;
      let suffix = "";

      if (absolute >= 1000000) {
        divisor = 1000000;
        suffix = "M";
      } else if (absolute >= 1000) {
        divisor = 1000;
        suffix = "K";
      }

      const compact = amount / divisor;
      const digits = suffix ? 1 : 2;
      return `$${compact.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: digits,
      })}${suffix}`;
    },
    fullCurrency(value) {
      return Number(value || 0).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
    },
    ...mapActions("invoice", ["ResetInvoice"]),

    showUpdatedDot(item) {
      if (!item || !item.createdAt || !item.updatedAt) {
        return false;
      }

      const created = this.$moment(item.createdAt).startOf("day");
      const updated = this.$moment(item.updatedAt).startOf("day");

      return !created.isSame(updated);
    },

    formatUpdatedAt(val) {
      if (!val) {
        return "";
      }

      return this.$moment(val).format("L LT");
    },

    openSignature(invoice) {
      this.selectedInvoice = invoice;
      this.signatureDialog = true;
    },

    openHistory(invoice) {
      if (!this.currentBusiness.metadata.historyTracking) {
        this.$swal({
          icon: "warning",
          title: "History Tracking Off",
          text: "Enable it in Settings to view change logs ($50/month).",
        });
        return;
      }

      this.historyInvoice = invoice;
      this.historyDialog = true;
    },

    async saveSignature({ invoiceId, payload }) {
      try {
        await invoiceService.updateMetadata(invoiceId, {
          deliverySignature: payload,
        });

        this.$swal("Success", "Signature saved to invoice.", "success");
        this.getList();
      } catch (err) {
        const message =
          err && err.data && err.data.message
            ? err.data.message
            : "Could not save signature.";

        this.$swal("Error", message, "error");
      }
    },

    dueDateColor(val) {
      const dueDate = val ? this.$moment(val) : this.$moment();
      const today = this.$moment().startOf("day");

      return dueDate.isBefore(today)
        ? "red--text font-weight-bold"
        : "black--text";
    },

    updateTotal(val) {
      this.listTotal = val.reduce((sum, row) => {
        return sum + Number(row.total || 0);
      }, 0);

      this.listPayment = val.reduce((sum, row) => {
        return sum + Number(row.payment || 0);
      }, 0);

      this.listBalance = val.reduce((sum, row) => {
        return sum + Number(row.due || 0);
      }, 0);
    },

    copyInvoice(item) {
      invoiceService.getById(item.id).then((obj) => {
        const passData = obj.data;
        passData.CustomerId = null;

        this.$router.push({
          name: "invoice-create",
          params: {
            passData,
            type: "invoice",
          },
        });
      });
    },

    receivePayment(row) {
      this.selectedInvoice = row;
      this.receivePaymentDialog = true;
    },

    openRunning() {
      if (this.getEditInvoiceId) {
        this.$router.push({ path: `/invoice/${this.getEditInvoiceId}/edit` });
        return;
      }

      this.$router.push({ path: "/invoice/create" });
    },

    viewOpenInvoice() {
      const hashId = hashids.encode(this.CustomerId);

      const routeData = this.$router.resolve({
        path: `/global/invoice/${hashId}/openInvoice`,
      });

      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },

    add() {
      if (this.InvoiceDetails.length !== 0) {
        this.$swal({
          title: "Are you sure?",
          text: "There is a running invoice. Do you want to clear it?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          cancelButtonText: "No",
          confirmButtonText: "Yes",
        }).then((result) => {
          if (result.value) {
            this.ResetInvoice();
            this.goToCreateInvoice();
          }
        });

        return;
      }

      this.goToCreateInvoice();
    },

    goToCreateInvoice() {
      if (this.CustomerId) {
        this.$router.push({
          path: `/invoice/create?CustomerId=${this.CustomerId}`,
        });
        return;
      }

      this.$router.push({ path: "/invoice/create" });
    },

    viewProfile(val) {
      if (!val.CustomerId) {
        return;
      }

      this.$router.push({ path: `/customer/${val.CustomerId}/profile` });
    },

    viewInvoice(invoice) {
      const hashId = hashids.encode(invoice.id);

      const routeData = this.$router.resolve({
        path: `/global/invoice/${hashId}/view`,
      });

      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },

    viewPackingSlip(invoice) {
      const hashId = hashids.encode(invoice.id);

      const routeData = this.$router.resolve({
        path: `/global/invoice/${hashId}/packingSlip`,
      });

      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },

    loadHeader() {
      this.headers = [
        {
          text: this.$t("labels.date"),
          align: "left",
          value: "date",
          sortable: true,
        },
        {
          text: "Due Date",
          align: "left",
          value: "dueDate",
          sortable: true,
        },
        {
          text: "Invoice Number",
          align: "left",
          value: "localId",
          sortable: true,
        },
      ];

      if (!this.CustomerId) {
        this.headers.push({
          text: "Customer Name",
          value: "Customer.name",
          align: "left",
          sortable: true,
        });
      }

      this.headers.push(
        {
          text: "Total",
          value: "total",
          align: "right",
          sortable: true,
        },
        {
          text: "Payment",
          value: "payment",
          align: "right",
          sortable: true,
        },
        {
          text: "Balance",
          value: "due",
          align: "right",
          sortable: true,
        },
        {
          text: "",
          value: "action",
          sortable: false,
        }
      );
    },

    getList() {
      this.loading = true;

      const where = {};

      const shouldUseDateRange =
        this.invoiceStatus !== "open" || !this.openAllDates;

      if (!this.showAll && shouldUseDateRange) {
        where.start = this.dateTime.start;
        where.end = this.dateTime.end;
      }

      if (this.invoiceStatus === "open") {
        where.due = true;
      }

      if (this.invoiceStatus === "paid") {
        where.paid = true;
      }

      if (!this.currentUser.isAdmin && this.checkRightStatus(50)) {
        where.assingedCustomer = this.currentUser.id;
      }

      if (this.CustomerId) {
        where.CustomerId = this.CustomerId;
      }

      if (this.due) {
        where.due = true;
      }

      const sortBy = Array.isArray(this.sortBy)
        ? this.sortBy[0] || "localId"
        : this.sortBy || "localId";

      const sortDesc = Array.isArray(this.sortDesc)
        ? !!this.sortDesc[0]
        : !!this.sortDesc;

      const query = {
        ...where,
        search: this.search || "",
        limit: this.itemsPerPage,
        offset: (this.page - 1) * this.itemsPerPage,
        sortBy,
        sortDesc,
        orderBy: [sortBy, String(sortDesc)],
      };

      return invoiceService
        .getAll(query)
        .then((response) => {
          const rows = response.data || [];

          this.totalItems = Number(response.count || 0);
          this.Invoices = rows;

          this.fullTotal = Number(
            response.totals && response.totals.total
              ? response.totals.total
              : 0
          );

          this.fullPayment = Number(
            response.totals && response.totals.payment
              ? response.totals.payment
              : 0
          );

          this.fullBalance = this.fullTotal - this.fullPayment;

          this.updateTotal(rows);

          return response;
        })
        .catch((err) => {
          console.error("invoice getList failed", err);
          this.Invoices = [];
          this.totalItems = 0;
          this.fullTotal = 0;
          this.fullPayment = 0;
          this.fullBalance = 0;
          this.listTotal = 0;
          this.listPayment = 0;
          this.listBalance = 0;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    editInvoice(invoice) {
      this.$router.push({ path: `/invoice/${invoice.id}/edit` });
    },

    deleteInvoice(invoice) {
      if (
        this.currentBusiness.metadata &&
        this.currentBusiness.metadata.deletePrecaution
      ) {
        this.$swal
          .fire({
            title: "Are you sure?",
            html: `You won't be able to revert this.</br> <div style="color:red;">Type <span style="font-weight: bold">delete</span> in order remove this</div>`,
            icon: "question",
            input: "text",
            inputAttributes: {
              autocapitalize: "off",
            },
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Delete",
          })
          .then((result) => {
            if (result.value && result.value === "delete") {
              this.deleteInvoiceConfirmed(invoice);
            } else if (result.dismiss !== "cancel") {
              this.$swal("Delete", 'Type "delete" to delete item', "error");
            }
          });

        return;
      }

      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          this.deleteInvoiceConfirmed(invoice);
        }
      });
    },

    deleteInvoiceConfirmed(invoice) {
      this.loadingDialog = true;

      invoiceService
        .delete(invoice.id)
        .then((response) => {
          if (response.data) {
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "success",
                title: "Invoice has been deleted.",
              });

            this.getList();
            this.$events.emit("customerProfileEvent");
            this.$events.emit("CustomerMiniList");
          }
        })
        .catch((error) => {
          const message =
            error && error.data && error.data.message
              ? error.data.message
              : "Unable to delete invoice";

          this.$swal({
            title: "Delete Invoice",
            text: message,
            icon: "error",
          });
        })
        .finally(() => {
          this.loadingDialog = false;
        });
    },
  },
};
</script>

<style scoped>
.invoice-list-page {
  background: #f8fafc;
}

.invoice-list-card {
  border-radius: 20px;
  background: #ffffff;
}

.invoice-list-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.invoice-title-wrap {
  display: flex;
  align-items: center;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
}

.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.invoice-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.invoice-filter-bar {
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.invoice-search {
  max-width: 340px;
  min-width: 260px;
}

.status-toggle {
  background: #f8fafc;
  border-radius: 14px;
  padding: 2px;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.summary-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 14px;
}

.summary-label {
  font-size: 12px;
  color: #64748b;
}

.summary-value {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin-top: 4px;
}

.invoice-table {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.invoice-row:hover {
  background: #f8fafc;
}

.mobile-filter-card {
  border-radius: 20px 20px 0 0;
}

.mobile-filter-header {
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.mobile-filter-title {
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
}

.mobile-status-toggle {
  width: 100%;
}

.mobile-status-toggle .v-btn {
  flex: 1;
}

.updated-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #f44336;
  box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7);
  animation: updated-dot-pulse 1.5s infinite;
}

@keyframes updated-dot-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7);
  }

  70% {
    box-shadow: 0 0 0 8px rgba(244, 67, 54, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
  }
}

@media (max-width: 960px) {
  .summary-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .invoice-list-header {
    flex-wrap: nowrap;
  }

  .invoice-search {
    max-width: none;
    min-width: 0;
    flex: 1;
  }

  .summary-row {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(150px, 44vw);
    grid-template-columns: none;
    gap: 8px;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scroll-snap-type: x proximity;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 4px;
    scrollbar-width: none;
  }

  .summary-row::-webkit-scrollbar {
    display: none;
  }

  .invoice-actions {
    width: auto;
    margin-left: auto;
  }

  .invoice-actions .new-list-button {
    width: 40px;
    min-width: 40px;
    height: 40px;
    padding: 0;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
  }

  .new-list-button .v-icon {
    margin: 0 !important;
  }

  .new-button-label {
    display: none;
  }

  .summary-box {
    padding: 12px;
    scroll-snap-align: start;
  }

  .summary-value {
    font-size: 18px;
  }

  .mobile-pagination {
    display: flex;
    justify-content: center;
  }

  .mobile-pagination::v-deep .v-pagination__item,
  .mobile-pagination::v-deep .v-pagination__navigation {
    background: #ffffff !important;
    border: 1px solid #cbd5e1;
    box-shadow: none;
  }

  .mobile-pagination::v-deep .v-pagination__item--active {
    color: var(--v-primary-base) !important;
    border-color: var(--v-primary-base);
    font-weight: 700;
  }
}
</style>
